import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  getEtegDashboardOverview,
  type EtegProductConsumption,
  type EtegSuccessError,
} from "@/services/apiCallsService";
import {
  getPlanUsage,
  type PlanUsageItem,
  type PlanUsageListResponse,
} from "@/services/plansService";

interface EtegHealthIndicatorsProps {
  startDate: Date;
  endDate: Date;
  month: number;
  year: number;
  clientId?: string | null;
  productId?: string | null;
}

const CHART_COLORS = [
  "#7c3aed",
  "#a78bfa",
  "#c4b5fd",
  "#8b5cf6",
  "#6d28d9",
  "#ddd6fe",
  "#4c1d95",
  "#5b21b6",
];

const PORTFOLIO_COLORS = {
  above: "#ef4444",
  attention: "#f59e0b",
  ok: "#22c55e",
};

const SUCCESS_COLORS = {
  success: "#22c55e",
  error: "#ef4444",
};

const PIE_LABEL_MAX_LEN = 22;
const PIE_LABEL_MIN_GAP = 14;
const PIE_LABEL_MAX_OFFSET = 104;
const RADIAN = Math.PI / 180;

function truncatePieLabel(name: string, max = PIE_LABEL_MAX_LEN): string {
  const text = String(name || "");
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function formatPiePercent(percent?: number): string {
  const value = (percent || 0) * 100;
  if (value > 0 && value < 1) return `${value.toFixed(1)}%`;
  return `${value.toFixed(0)}%`;
}

interface PieLabelPlacement {
  dy: number;
  side: "left" | "right";
}

function buildPieLabelPlacements(
  data: Array<{ value: number }>,
  outerRadius: number,
): Map<number, PieLabelPlacement> {
  const placements = new Map<number, PieLabelPlacement>();
  const total = data.reduce((acc, item) => acc + (Number(item.value) || 0), 0);
  if (total <= 0) return placements;

  const radius = outerRadius + 14;
  let startAngle = 0;
  const items = data.map((item, index) => {
    const percent = (Number(item.value) || 0) / total;
    const midAngle = startAngle + (percent * 360) / 2;
    startAngle += percent * 360;
    return {
      index,
      naturalY: radius * Math.sin(-midAngle * RADIAN),
      side: (Math.cos(-midAngle * RADIAN) >= 0 ? "right" : "left") as
        | "left"
        | "right",
    };
  });

  (["left", "right"] as const).forEach((side) => {
    const sorted = items
      .filter((item) => item.side === side)
      .sort((a, b) => a.naturalY - b.naturalY);
    if (sorted.length === 0) return;

    let adjusted = sorted.map((item) => item.naturalY);
    for (let i = 1; i < adjusted.length; i += 1) {
      const current = adjusted[i] ?? 0;
      const previous = adjusted[i - 1] ?? current;
      adjusted[i] = Math.max(current, previous + PIE_LABEL_MIN_GAP);
    }
    const last = adjusted[adjusted.length - 1] ?? 0;
    if (last > PIE_LABEL_MAX_OFFSET) {
      const shift = last - PIE_LABEL_MAX_OFFSET;
      adjusted = adjusted.map((y) => y - shift);
    }
    const first = adjusted[0] ?? 0;
    if (first < -PIE_LABEL_MAX_OFFSET) {
      const shift = -PIE_LABEL_MAX_OFFSET - first;
      adjusted = adjusted.map((y) => y + shift);
    }
    for (let i = adjusted.length - 2; i >= 0; i -= 1) {
      const current = adjusted[i] ?? 0;
      const next = adjusted[i + 1] ?? current;
      adjusted[i] = Math.min(current, next - PIE_LABEL_MIN_GAP);
    }

    sorted.forEach((item, i) => {
      placements.set(item.index, {
        dy: (adjusted[i] ?? item.naturalY) - item.naturalY,
        side,
      });
    });
  });

  return placements;
}

function createPieSliceLabel(
  placements: Map<number, PieLabelPlacement>,
  colors: string[],
) {
  return function PieSliceLabel({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    percent,
    index,
  }: {
    cx?: number;
    cy?: number;
    midAngle?: number;
    outerRadius?: number;
    name?: string;
    percent?: number;
    index?: number;
  }) {
    if (
      cx == null ||
      cy == null ||
      midAngle == null ||
      outerRadius == null
    ) {
      return null;
    }

    const placement = index != null ? placements.get(index) : undefined;
    const radius = outerRadius + 14;
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const x = cx + radius * cos;
    const y = cy + radius * sin + (placement?.dy ?? 0);
    const isRight = placement ? placement.side === "right" : x > cx;
    const textX = x + (isRight ? 5 : -5);
    const edgeX = cx + outerRadius * cos;
    const edgeY = cy + outerRadius * sin;
    const color =
      colors[(index ?? 0) % Math.max(colors.length, 1)] ??
      CHART_COLORS[(index ?? 0) % CHART_COLORS.length] ??
      "#7c3aed";
    const fullName = name || "";

    return (
      <g>
        <polyline
          points={`${edgeX},${edgeY} ${x},${y} ${textX},${y}`}
          fill="none"
          stroke={color}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y}
          fill="hsl(var(--foreground))"
          textAnchor={isRight ? "start" : "end"}
          dominantBaseline="central"
          fontSize={10}
          fontWeight={500}
        >
          <title>{fullName}</title>
          {`${truncatePieLabel(fullName)} ${formatPiePercent(percent)}`}
        </text>
      </g>
    );
  };
}


function isListResponse(
  data: PlanUsageItem | PlanUsageListResponse,
): data is PlanUsageListResponse {
  return "clients" in data;
}

function usageRatio(used: number, limit: number): number {
  if (!limit || limit <= 0) return used > 0 ? 999 : 0;
  return (used / limit) * 100;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground text-center px-4">
      {message}
    </div>
  );
}

function ChartCard({
  title,
  description,
  loading,
  children,
}: {
  title: string;
  description?: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? (
          <CardDescription>{description}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex h-[220px] items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando...
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

export function EtegHealthIndicators({
  startDate,
  endDate,
  month,
  year,
  clientId,
  productId,
}: EtegHealthIndicatorsProps) {
  const [loadingScoped, setLoadingScoped] = useState(true);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);
  const [byProduct, setByProduct] = useState<EtegProductConsumption[]>([]);
  const [successError, setSuccessError] = useState<EtegSuccessError | null>(
    null,
  );
  const [planClients, setPlanClients] = useState<PlanUsageItem[]>([]);

  const scopedClientId =
    clientId && clientId !== "all"
      ? clientId === "sandbox"
        ? "null"
        : clientId
      : undefined;

  useEffect(() => {
    let cancelled = false;

    const loadScoped = async () => {
      setLoadingScoped(true);
      try {
        const response = await getEtegDashboardOverview({
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          client_id: scopedClientId,
          product_id: productId && productId !== "all" ? productId : undefined,
        });
        if (cancelled) return;
        if (!response.success || !response.data) {
          setByProduct([]);
          setSuccessError(null);
          return;
        }
        setByProduct(response.data.by_product || []);
        setSuccessError(response.data.success_error);
      } catch {
        if (!cancelled) {
          setByProduct([]);
          setSuccessError(null);
        }
      } finally {
        if (!cancelled) setLoadingScoped(false);
      }
    };

    void loadScoped();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, scopedClientId, productId]);

  useEffect(() => {
    let cancelled = false;

    const loadPortfolio = async () => {
      setLoadingPortfolio(true);
      try {
        const response = await getPlanUsage({ month, year });
        if (cancelled) return;
        if (!response.success || !response.data) {
          setPlanClients([]);
          return;
        }
        const data = response.data;
        setPlanClients(isListResponse(data) ? data.clients || [] : [data]);
      } catch {
        if (!cancelled) setPlanClients([]);
      } finally {
        if (!cancelled) setLoadingPortfolio(false);
      }
    };

    void loadPortfolio();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const specialistData = useMemo(() => {
    const total = byProduct.reduce((acc, item) => acc + item.calls, 0);
    if (total <= 0) return [];
    return byProduct.map((item) => ({
      name: item.product_name,
      value: item.calls,
    }));
  }, [byProduct]);

  const portfolioData = useMemo(() => {
    let above = 0;
    let attention = 0;
    let ok = 0;

    for (const item of planClients) {
      const usedCalls = item.in_plan_calls + item.excess_calls;
      const usedTokens = item.in_plan_tokens + item.excess_tokens;
      const callsPct = usageRatio(usedCalls, Number(item.plan?.call_limit || 0));
      const tokensPct = usageRatio(
        usedTokens,
        Number(item.plan?.token_limit || 0),
      );
      const maxPct = Math.max(callsPct, tokensPct);
      if (maxPct > 100) above += 1;
      else if (maxPct >= 80) attention += 1;
      else ok += 1;
    }

    const total = above + attention + ok;
    if (total === 0) return [];

    return [
      {
        name: "Acima do limite",
        value: above,
        share: (above / total) * 100,
        color: PORTFOLIO_COLORS.above,
      },
      {
        name: "Atenção",
        value: attention,
        share: (attention / total) * 100,
        color: PORTFOLIO_COLORS.attention,
      },
      {
        name: "Dentro do limite",
        value: ok,
        share: (ok / total) * 100,
        color: PORTFOLIO_COLORS.ok,
      },
    ].filter((item) => item.value > 0);
  }, [planClients]);

  const revenueByPlan = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of planClients) {
      const planName = item.plan?.name || "Sem plano";
      map.set(planName, (map.get(planName) || 0) + Number(item.excess_cost || 0));
    }
    const entries = Array.from(map.entries()).filter(([, value]) => value > 0);
    const total = entries.reduce((acc, [, value]) => acc + value, 0);

    if (total <= 0) {
      const counts = new Map<string, number>();
      for (const item of planClients) {
        const planName = item.plan?.name || "Sem plano";
        counts.set(planName, (counts.get(planName) || 0) + 1);
      }
      const countTotal = Array.from(counts.values()).reduce((a, b) => a + b, 0);
      if (countTotal === 0) return [];
      return Array.from(counts.entries()).map(([name, value]) => ({
        name,
        value,
        mode: "clients" as const,
      }));
    }

    return entries.map(([name, value]) => ({
      name,
      value,
      mode: "revenue" as const,
    }));
  }, [planClients]);

  const specialistPlacements = useMemo(
    () => buildPieLabelPlacements(specialistData, 68),
    [specialistData],
  );
  const revenuePlacements = useMemo(
    () => buildPieLabelPlacements(revenueByPlan, 68),
    [revenueByPlan],
  );

  const successData = useMemo(() => {
    if (!successError || successError.total <= 0) return [];
    return [
      {
        name: "Sucesso",
        value: successError.success,
        color: SUCCESS_COLORS.success,
      },
      {
        name: "Erro",
        value: successError.error,
        color: SUCCESS_COLORS.error,
      },
    ].filter((item) => item.value > 0);
  }, [successError]);

  const successPlacements = useMemo(
    () => buildPieLabelPlacements(successData, 68),
    [successData],
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <ChartCard
        title="Consumo por Especialista (global)"
        description="Reage ao período e ao cliente filtrado"
        loading={loadingScoped}
      >
        {specialistData.length === 0 ? (
          <EmptyState message="Sem dados de especialistas no período selecionado." />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart margin={{ top: 28, right: 16, bottom: 8, left: 16 }}>
              <Pie
                data={specialistData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={68}
                label={createPieSliceLabel(
                  specialistPlacements,
                  specialistData.map(
                    (_, index) => CHART_COLORS[index % CHART_COLORS.length] ?? "#7c3aed",
                  ),
                )}
                labelLine={false}
              >
                {specialistData.map((_, index) => (
                  <Cell
                    key={`spec-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value.toLocaleString("pt-BR")} acionamentos`,
                  name,
                ]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Situação da Carteira"
        description="Sempre considera todos os clientes"
        loading={loadingPortfolio}
      >
        {portfolioData.length === 0 ? (
          <EmptyState message="Sem dados de carteira no período selecionado." />
        ) : (
          <div className="flex h-[220px] flex-col justify-between">
            <div aria-hidden className="flex-1" />
            <div className="space-y-5 pb-1">
              <div className="flex h-10 w-full overflow-hidden rounded-lg shadow-sm">
                {portfolioData.map((segment) => (
                  <div
                    key={segment.name}
                    className="h-full flex items-center justify-center text-xs font-semibold text-white"
                    style={{
                      width: `${segment.share}%`,
                      backgroundColor: segment.color,
                      backgroundImage:
                        segment.name === "Acima do limite"
                          ? "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.2) 4px, rgba(255,255,255,0.2) 8px)"
                          : segment.name === "Atenção"
                            ? "repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 6px)"
                            : undefined,
                    }}
                    title={`${segment.name}: ${segment.value}`}
                  >
                    {segment.share >= 12
                      ? `${Math.round(segment.share)}%`
                      : ""}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                {portfolioData.map((segment) => (
                  <div key={segment.name} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: segment.color }}
                    />
                    <span>
                      <span className="text-foreground font-medium">
                        {segment.name}
                      </span>
                      : {segment.value} ({Math.round(segment.share)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </ChartCard>

      <ChartCard
        title="Receita por Plano"
        description="Sempre considera todos os clientes"
        loading={loadingPortfolio}
      >
        {revenueByPlan.length === 0 ? (
          <EmptyState message="Sem dados de receita por plano no período selecionado." />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart margin={{ top: 28, right: 16, bottom: 8, left: 16 }}>
              <Pie
                data={revenueByPlan}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={68}
                label={createPieSliceLabel(
                  revenuePlacements,
                  revenueByPlan.map(
                    (_, index) => CHART_COLORS[index % CHART_COLORS.length] ?? "#7c3aed",
                  ),
                )}
                labelLine={false}
              >
                {revenueByPlan.map((_, index) => (
                  <Cell
                    key={`rev-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props) => {
                  const mode = (props?.payload as { mode?: string })?.mode;
                  if (mode === "clients") {
                    return [`${value} cliente(s)`, name];
                  }
                  return [
                    Number(value).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }),
                    name,
                  ];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Taxa de Sucesso x Erro (global)"
        description="Reage ao período e ao cliente filtrado"
        loading={loadingScoped}
      >
        {successData.length === 0 ? (
          <EmptyState message="Sem chamadas no período selecionado." />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart margin={{ top: 28, right: 16, bottom: 8, left: 16 }}>
              <Pie
                data={successData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={68}
                label={createPieSliceLabel(
                  successPlacements,
                  successData.map((entry) => entry.color),
                )}
                labelLine={false}
              >
                {successData.map((entry, index) => (
                  <Cell key={`se-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${value.toLocaleString("pt-BR")} chamadas`,
                  name,
                ]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}
